import React, { useMemo, useRef } from 'react';
import type { CellContext } from '@tanstack/react-table';
import { useRootStore } from 'store';
import {
  TablePaginatedV8,
  TableDataState,
  CellLeftV8,
  EditCellV8,
  TableCellV8,
  toastWarning,
} from 'motion-components';
import type { TablePaginatedV8Ref } from 'motion-components';
import { Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormikContext } from 'formik';
// import { TablePaginatedV8 } from 'table-v8/table-paginated-v8';

type IdocExtraRow = {
  id?: number | string | null;
  idocId?: number | null;
  containertypeId?: number | null;
  containerTypeName?: string | null;
  containersQty?: number | string | null;
  containerTareWeight?: number | string | null;
  containertype?: {
    id?: number | null;
    name?: string | null;
  } | null;
};

/* ----------- Component ------------------ */
export const IDocExtrasTable = ({ idocId, data, canEdit }) => {
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const { values, setFieldValue, isSubmitting, dirty } = useFormikContext<any>();
  const gridRef = useRef<TablePaginatedV8Ref<IdocExtraRow> | null>(null);
  const tableData = values.idocExtras ?? data ?? [];

  const columns = useMemo(
    () => [
      {
        id: 'containerTypeName',
        header: t('Τύπος Συσκευασίας'),
        columnName: t('Τύπος Συσκευασίας'),
        accessorFn: (row: IdocExtraRow) =>
          row.containertypeId == null ? row.containerTypeName ?? '' : row.containertype?.name ?? '',
        orderByField: 'containerTypeName',
        cell: (props: CellContext<IdocExtraRow, unknown>) =>
          props.row.original.containertypeId == null ? (
            <TableCellV8 {...props} />
          ) : (
            <CellLeftV8 {...props} />
          ),
        meta: { type: 'text', noEnter: true, required: true },
        size: 5,
      },
      {
        id: 'containersQty',
        header: t('Πλήθος Συσκευασιών'),
        columnName: t('Πλήθος Συσκευασιών'),
        accessorKey: 'containersQty',
        orderByField: 'containersQty',
        cell: TableCellV8,
        meta: { type: 'float', noEnter: true },
        size: 30,
      },
      {
        id: 'containerTareWeight',
        header: t('Απόβαρο Συσκευασίας'),
        columnName: t('Απόβαρο Συσκευασίας'),
        accessorKey: 'containerTareWeight',
        orderByField: 'containerTareWeight',
        cell: TableCellV8,
        meta: { type: 'float', noEnter: true },
        size: 30,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        orderByField: 'lastUser',
        size: 120,
        cell: (props: CellContext<IdocExtraRow, unknown>) => {
          const { row, table } = props;
          return canEdit ? EditCellV8({ row, table }) : <div className="text-center">-</div>;
        },
      },
    ],
    [canEdit, t],
  );

  // const tableRef = useRef('aaa');
  // // // return of useRef
  // useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  const onSaveRow = async (rowIndex: number, row: IdocExtraRow, changes: Partial<IdocExtraRow>) => {
    //
    // i.e. Save Record
    //

    const rawTareWeight =
      changes.containerTareWeight !== undefined
        ? changes.containerTareWeight
        : row.containerTareWeight;
    const tareWeightText = String(rawTareWeight ?? '').trim().replace(',', '.');
    const tareWeight = tareWeightText === '' ? null : Number(tareWeightText);
    if (tareWeight !== null && !Number.isFinite(tareWeight)) {
      toastWarning(t('Το απόβαρο συσκευασίας πρέπει να είναι έγκυρος αριθμός.'));
      return false;
    }

    // Κράτα αντίγραφο των κάδων
    let lcIdocExtras = JSON.parse(JSON.stringify(values.idocExtras ?? []));
    console.log('$$$values.idocExtras= ', values.idocExtras);
    console.log('$$$lcIdocExtras= ', lcIdocExtras);

    if (row.id === '*') {
      const containerTypeName = String(
        changes.containerTypeName ?? row.containerTypeName ?? '',
      ).trim();
      if (!containerTypeName) return false;

      lcIdocExtras.push({
        ...row,
        ...changes,
        id: null,
        idocId: idocId ?? null,
        containertypeId: null,
        containerTypeName,
        containersQty: changes.containersQty ?? row.containersQty ?? null,
        containerTareWeight: tareWeight,
      });
      await setFieldValue('idocExtras', lcIdocExtras);
    } else {
      // Ενημέρωσε τις Τιμές του Formik
      if (changes.containerTypeName !== undefined) {
        setFieldValue(`idocExtras[${rowIndex}].containerTypeName`, changes.containerTypeName);
        lcIdocExtras[rowIndex].containerTypeName = changes.containerTypeName;
      }
      if (changes.containertypeId) {
        setFieldValue(`idocExtras[${rowIndex}].containertypeId`, changes.containertypeId);
        lcIdocExtras[rowIndex].containertypeId = changes.containertypeId;
      } else {
        // setFieldValue(`idocExtras[${rowIndex}].containertypeId`, null);
        // lcIdocExtras[rowIndex].containertypeId = 0;
      }
      if (changes.containersQty !== undefined) {
        setFieldValue(`idocExtras[${rowIndex}].containersQty`, changes.containersQty);
        lcIdocExtras[rowIndex].containersQty = changes.containersQty;
      } else {
        // Preserve the existing value instead of setting it to null
        lcIdocExtras[rowIndex].containersQty = values.idocExtras[rowIndex].containersQty;
      }
      if (changes.containerTareWeight !== undefined) {
        setFieldValue(`idocExtras[${rowIndex}].containerTareWeight`, tareWeight);
        lcIdocExtras[rowIndex].containerTareWeight = tareWeight;
      } else {
        // Preserve the existing value instead of setting it to null
        lcIdocExtras[rowIndex].containerTareWeight =
          values.idocExtras[rowIndex].containerTareWeight;
      }
    }

    // Υπολόγισε Άθροισμα Κάδων και Απόβαρου
    let sumContQty = 0;
    let sumContTareWeight = 0;
    for (let index = 0; index < lcIdocExtras.length; index++) {
      const element = lcIdocExtras[index];
      const qty = Number.isFinite(Number(element.containersQty))
        ? Number(element.containersQty)
        : 0;
      const tare = Number.isFinite(Number(element.containerTareWeight))
        ? Number(element.containerTareWeight)
        : 0;
      console.log('$$$$ qty tare', qty, tare, element.containerTareWeight);
      console.log(`Index ${index} -> Qty: ${qty}, Tare: ${tare}, Partial Weight: ${qty * tare}`);
      sumContQty = sumContQty + qty;
      sumContTareWeight += qty * tare;
      console.log(
        `Index ${index} -> Qty: ${qty}, Tare: ${tare}, Running Total: ${sumContTareWeight}`,
      );
    }
    // Ενημέρωσε Αθροίσματα στο Header
    console.log('$$$sumContTareWeight= ', sumContTareWeight);
    setFieldValue('containersQty', sumContQty);
    setFieldValue('tareWeight', sumContTareWeight);

    //return id;
    return true; // για να γυρίσει σε browse ο πίνακας
  };

  return (
    <>
      <div className="form-group-title d-flex justify-content-between align-items-center">
        <h5 style={{ marginBottom: '0px' }}>{t('Πρόσθετα Στοιχεία Συσκευασιών')}</h5>
        <Button
          type="button"
          size="sm"
          variant="success"
          disabled={!canEdit || tableStore.state !== TableDataState.browse}
          onClick={() => gridRef.current?.table?.options?.meta?.addNewRow()}
        >
          <FaPlus className="me-1" />
          {t('Προσθήκη Συσκευασίας')}
        </Button>
      </div>

      {/* TableGrid */}
      {data ? (
        <TablePaginatedV8
          ref={gridRef}
          loading={false}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={tableData}
          dataCount={tableData.length}
          topBorderRadius={false}
          initSorting={[{ id: 'containerTypeName', desc: true }]}
          onSaveRow={onSaveRow}
          onNewRow={() => ({
            id: '*',
            idocId: idocId ?? null,
            containertypeId: null,
            containerTypeName: '',
            containersQty: null,
            containerTareWeight: null,
          })}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </>
  );
};
